import { useCallback, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { ApiErrors, PixType } from '../../enums';
import { useRegisterPixKeyMutation, useRegisterPixKeyV2Mutation } from '../../services/pixKeysApi';
import { unformatValue } from '../../utils/pixFormatters';
import { usePinModal } from '../usePinModal';
export function usePixKeyValidationNavigation({ onNavigateToConfirmation, onValidationError, }) {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag(undefined, { autoLoad: false });
    const isBaasV2 = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const { unmaskCnpj } = useCnpjAlphanumeric();
    const [registerPixKey] = useRegisterPixKeyMutation();
    const [registerPixKeyV2] = useRegisterPixKeyV2Mutation();
    const [selectedPixKey, setSelectedPixKey] = useState(null);
    const handlePinConfirm = useCallback(async (pin) => {
        if (!selectedPixKey)
            return;
        try {
            let formattedKeyValue = selectedPixKey.key;
            if (selectedPixKey.type === PixType.PHONE) {
                formattedKeyValue = selectedPixKey.key.startsWith('+55')
                    ? `+55${unformatValue(selectedPixKey.key.substring(3))}`
                    : `+55${unformatValue(selectedPixKey.key)}`;
            }
            else if (selectedPixKey.type === PixType.TAX_IDENTIFIER) {
                formattedKeyValue = unmaskCnpj(selectedPixKey.key);
            }
            const request = {
                credential: pin,
                key: formattedKeyValue,
                pixType: selectedPixKey.type,
            };
            const registerFn = isBaasV2 ? registerPixKeyV2 : registerPixKey;
            const result = await registerFn(request).unwrap();
            const responseData = result.result || result;
            const isConfirmationRequired = selectedPixKey.type === PixType.EMAIL || selectedPixKey.type === PixType.PHONE;
            if (isConfirmationRequired && responseData.protocolId) {
                onNavigateToConfirmation(responseData.protocolId, responseData.key, selectedPixKey.type);
            }
            else {
                onValidationError(t('pix-keys.validation.success-no-confirmation'));
            }
        }
        catch (error) {
            const errorObj = error;
            const errorCode = errorObj?.result?.error;
            if (errorCode === ApiErrors.PIN_FAILURE_TO_VALIDATE) {
                throw error;
            }
            onValidationError(t('pix-keys.validation.error'));
        }
    }, [
        selectedPixKey,
        isBaasV2,
        registerPixKeyV2,
        registerPixKey,
        onNavigateToConfirmation,
        onValidationError,
        t,
        unmaskCnpj,
    ]);
    const { showPinModal, isCreating, showPinError, pinErrorMessage, handlePinConfirm: pinModalHandlePinConfirm, handlePinCancel, handlePinClose, handlePinErrorClose, openPinModal, } = usePinModal({
        onConfirm: handlePinConfirm,
    });
    const handleValidatePixKey = useCallback((pixKey) => {
        setSelectedPixKey(pixKey);
        openPinModal();
    }, [openPinModal]);
    return {
        showPinModal,
        isCreating,
        showPinError,
        pinErrorMessage,
        handleValidatePixKey,
        handlePinConfirm: pinModalHandlePinConfirm,
        handlePinCancel,
        handlePinClose,
        handlePinErrorClose,
    };
}
